import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/shared/ui/dialog";
import { Button } from "@/components/shared/ui/button";
import { ScrollArea } from "@/components/shared/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { Globe } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "react-i18next";
// @ts-expect-error - Vite raw import not typed
import changelogContent from "/CHANGELOG.md?raw";
import { logger } from "@/lib/logger";

interface ChangelogEntry {
  id: string;
  version: string;
  title: string;
  description: string;
  changes: ChangelogChange[];
  release_date: string;
  created_at: string;
}

interface ChangelogChange {
  type: "feature" | "bug" | "improvement" | "security";
  category: string;
  description: string;
}

interface ChangelogPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChangelogPopup = ({ open, onOpenChange }: ChangelogPopupProps) => {
  const { t } = useTranslation();
  const [changelogEntries, setChangelogEntries] = useState<ChangelogEntry[]>(
    []
  );
  const [_loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchChangelog();
    }
  }, [open]);

  const fetchChangelog = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("changelog")
        .select("*")
        .order("release_date", { ascending: false })
        .limit(5);

      if (error) throw error;

      const formattedData = (data || []).map((entry) => ({
        ...entry,
        changes: Array.isArray(entry.changes)
          ? (entry.changes as unknown as ChangelogChange[])
          : [],
      }));
      setChangelogEntries(formattedData);
    } catch (error) {
      logger.error("Error fetching changelog:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = () => {
    // Store the latest version in localStorage to prevent showing again
    if (changelogEntries.length > 0) {
      localStorage.setItem(
        "lastSeenChangelogVersion",
        changelogEntries[0].version
      );
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {t("changelog.whatsNew")}
              </DialogTitle>
              <DialogDescription>
                {t("changelog.latestUpdates")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {changelogContent}
            </ReactMarkdown>
          </div>
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-4">
          <Button onClick={markAsRead}>{t("changelog.markAsRead")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
